import { useState } from "react";
import { Mail, CheckCircle2, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

interface Props {
  /** Where the sign-up happened (chat, article, footer ...) */
  source: string;
  /** Optional question the user asked in the chat assistant */
  question?: string;
  variant?: "section" | "compact";
  className?: string;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const NewsletterSignup = ({ source, question, variant = "section", className = "" }: Props) => {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<"idle" | "saving" | "done" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const value = email.trim().toLowerCase();
    if (!EMAIL_RE.test(value) || value.length > 255) {
      setError("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      return;
    }
    if (!consent) {
      setError("Bitte bestätigen Sie die Einwilligung.");
      return;
    }
    setStatus("saving");
    const { error: dbError } = await supabase
      .from("newsletter_subscribers")
      .insert({ email: value, consent: true, source, question: question ?? null });

    // Duplicate e-mail = already subscribed, treat as success
    if (dbError && !dbError.message.includes("duplicate")) {
      console.error("Newsletter-Anmeldung fehlgeschlagen:", dbError);
      setStatus("error");
      setError("Anmeldung derzeit nicht möglich. Bitte später erneut versuchen.");
      return;
    }
    setStatus("done");
  };

  if (status === "done") {
    return (
      <div
        className={`flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4 text-sm ${className}`}
        role="status"
      >
        <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
        <p className="text-muted-foreground">
          <span className="font-semibold text-foreground">Danke für Ihre Anmeldung!</span> Sie erhalten ab
          sofort einmal pro Woche unsere Tipps zu neuen Gewerbeflächen-Angeboten.
        </p>
      </div>
    );
  }

  const compact = variant === "compact";

  return (
    <section
      className={`rounded-lg border border-border bg-muted/30 ${compact ? "p-4" : "p-6"} ${className}`}
      aria-labelledby={`newsletter-${source}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Mail className={`${compact ? "h-4 w-4" : "h-5 w-5"} text-primary`} aria-hidden="true" />
        <h2 id={`newsletter-${source}`} className={`font-semibold ${compact ? "text-sm" : "text-lg"}`}>
          Wöchentliche Tipps zu neuen Gewerbeflächen
        </h2>
      </div>
      <p className={`text-muted-foreground mb-4 ${compact ? "text-xs" : "text-sm"}`}>
        Einmal pro Woche: die aktuellsten Angebote, Marktdaten und praktische Tipps zur Suche nach
        Gewerbeimmobilien in Deutschland. Jederzeit abbestellbar.
      </p>

      <form onSubmit={submit} className="space-y-3">
        <div className={compact ? "space-y-2" : "flex flex-col sm:flex-row gap-2"}>
          <Input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ihre@email.de"
            aria-label="E-Mail-Adresse"
            maxLength={255}
            className="bg-background"
          />
          <Button type="submit" disabled={status === "saving"} className="shrink-0">
            {status === "saving" ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                Wird gesendet
              </>
            ) : (
              "Newsletter abonnieren"
            )}
          </Button>
        </div>

        <div className="flex items-start gap-2">
          <Checkbox
            id={`consent-${source}`}
            checked={consent}
            onCheckedChange={(v) => setConsent(v === true)}
            className="mt-0.5"
          />
          <label htmlFor={`consent-${source}`} className="text-xs leading-snug text-muted-foreground">
            Ja, ich möchte den kostenlosen Newsletter mit wöchentlichen Tipps und Angeboten zu
            Gewerbeimmobilien per E-Mail erhalten. Meine Einwilligung kann ich jederzeit mit Wirkung für
            die Zukunft widerrufen. Weitere Informationen in der{" "}
            <Link to="/datenschutz" className="underline underline-offset-2 hover:text-primary">
              Datenschutzerklärung
            </Link>
            .
          </label>
        </div>

        {error && (
          <p className="text-xs text-destructive" role="alert">
            {error}
          </p>
        )}
      </form>
    </section>
  );
};
